import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { number, object, string } from "yup";
import PropTypes from "prop-types";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { ChartOfAccountTypeEnum } from "@/features/chart-of-accounts";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import { CustomDialogTitle } from "../custom-dialog-title";
import { CustomInputField } from "../custom-input-field";
import { CustomNumberField } from "../custom-number-field";

const DEFAULT_ALLOWED_OCOA_TYPES = [
  ChartOfAccountTypeEnum.REVENUE,
  ChartOfAccountTypeEnum.COST_OF_GOODS_SOLD,
  ChartOfAccountTypeEnum.EXPENSES,
  ChartOfAccountTypeEnum.OTHER_EXPENSES,
  ChartOfAccountTypeEnum.OTHER_INCOME,
  ChartOfAccountTypeEnum.CURRENT_LIABILITIES,
  ChartOfAccountTypeEnum.OTHER_CURRENT_ASSETS,
];

export const ExpenseDialog = ({
  outletId,
  onClose,
  onSubmit,
  defaultValues,
  title = "Tambah Biaya",
  amountLabel = "Jumlah",
  allowedAccountTypes = DEFAULT_ALLOWED_OCOA_TYPES,
  // Documents whose backend rejects a zero line (expense accruals, other
  // payments) opt in; the invoice/return callers keep the historic min-0 rule.
  requirePositiveAmount = false,
  // Set for a line whose account is dictated by a source document — editing it
  // would desync the two documents, so the backend rejects a mismatch.
  accountDisabled = false,
}) => {
  const schema = useMemo(() => {
    const amount = number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .typeError("Harus berupa angka")
      .required("Harus diisi");

    return object().shape({
      outletChartOfAccount: object().required("Harus diisi"),
      amount: requirePositiveAmount
        ? amount.moreThan(0, "Harus lebih dari 0")
        : amount.min(0, "Tidak boleh negatif"),
      note: string().nullable(),
    });
  }, [requirePositiveAmount]);

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      outletChartOfAccount: null,
      // Empty, not 0: CustomNumberField renders "" as a blank input, so the
      // user types straight into it instead of clearing a prefilled zero.
      amount: "",
      note: "",
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const handleClose = () => {
    onClose();
  };

  const onFormSubmit = (data) => {
    onSubmit(data);
    handleClose();
  };

  return (
    <Dialog open onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title={title}
        onClose={handleClose}
      />
      <DialogContent dividers>
        <Stack
          spacing={2}
          component="form"
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="outletChartOfAccount"
                render={({ field, fieldState }) => (
                  <OutletChartOfAccountSelect
                    outletId={outletId}
                    types={allowedAccountTypes}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={accountDisabled}
                    TextFieldProps={{
                      required: true,
                      label: "Akun",
                      error: fieldState.invalid,
                      helperText:
                        accountDisabled
                          ? "Akun mengikuti dokumen sumber"
                          : fieldState.error?.message,
                      InputProps: {
                        placeholder: "Pilih",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="amount"
                render={({ field, fieldState }) => (
                  <CustomNumberField
                    label={amountLabel}
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">Rp</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                control={control}
                name="note"
                render={({ field, fieldState }) => (
                  <CustomInputField
                    label="Catatan"
                    value={field.value}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Batal</Button>
        <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExpenseDialog.propTypes = {
  outletId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  title: PropTypes.string,
  amountLabel: PropTypes.string,
  allowedAccountTypes: PropTypes.arrayOf(PropTypes.string),
  requirePositiveAmount: PropTypes.bool,
  accountDisabled: PropTypes.bool,
};
